import { Injectable } from '@angular/core';
import { ColumnDef } from '@app/shared/types/ui';

@Injectable({
  providedIn: 'root'
})
export class TableStorageService {
  private storagePrefix = 'bindErp_table_';

  saveColumnSettings(key: string, columns: ColumnDef[]): void {
    if (!Array.isArray(columns)) {
      return;
    }

    try {
      const storageKey = this.getStorageKey(key);
      localStorage.setItem(storageKey, JSON.stringify(columns));
    } catch (error) {
      console.error('Failed to save column settings:', error);
    }
  }

  private getStorageKey(key: string): string {
    return `${this.storagePrefix}${key}`;
  }

  loadColumnSettings(key: string, defaultColumns: ColumnDef[]): ColumnDef[] {
    if (!Array.isArray(defaultColumns)) {
      return [];
    }

    try {
      const storageKey = this.getStorageKey(key);
      const saved = localStorage.getItem(storageKey);

      if (saved) {
        const parsedColumns = JSON.parse(saved);
        if (Array.isArray(parsedColumns)) {
          return this.mergeWithDefaultColumns(parsedColumns as ColumnDef[], defaultColumns);
        }
      }
    } catch (e) {
      console.error('Error loading column settings', e);
    }

    return [...defaultColumns];
  }

  private mergeWithDefaultColumns(savedColumns: ColumnDef[], defaultColumns: ColumnDef[]): ColumnDef[] {
    if (!Array.isArray(savedColumns) || !Array.isArray(defaultColumns)) {
      return Array.isArray(defaultColumns) ? [...defaultColumns] : [];
    }

    const result: ColumnDef[] = JSON.parse(JSON.stringify(defaultColumns));
    for (const savedCol of savedColumns) {
      if (this.isValidColumnDef(savedCol)) {
        const index = result.findIndex(col => col.key === savedCol.key);
        if (index !== -1) {
          result[index].visible = savedCol.visible;
        }
      }
    }

    const orderedResult: ColumnDef[] = [];
    const processedKeys = new Set<string>();

    for (const savedCol of savedColumns) {
      if (this.isValidColumnDef(savedCol)) {
        const found = result.find(col => col.key === savedCol.key);
        if (found) {
          orderedResult.push(found);
          processedKeys.add(savedCol.key);
        }
      }
    }

    for (const col of result) {
      if (!processedKeys.has(col.key)) {
        orderedResult.push(col);
      }
    }

    return orderedResult;
  }

  private isValidColumnDef(obj: unknown): obj is ColumnDef {
    return Boolean(
      obj &&
      typeof obj === 'object' &&
      'key' in obj &&
      typeof obj.key === 'string'
    );
  }
}
